<template>
  <section class="last-working-week-view">
    <section
      v-for="section in sections"
      :key="section.title"
      class="mode-card mode-card--accordion"
    >
      <button
        class="accordion-header"
        type="button"
        :aria-expanded="isSectionExpanded(section.title)"
        @click="toggleSection(section.title)"
      >
        <b-icon
          :icon="isSectionExpanded(section.title) ? 'chevron-down' : 'chevron-right'"
          size="is-small"
          class="accordion-chevron"
        />
        <span class="section-label">{{ section.title }}</span>
      </button>

      <div v-show="isSectionExpanded(section.title)" class="accordion-body">
        <p
          v-if="section.type === 'text'"
          class="mode-text"
          v-html="linkifyTicketRefs(section.text)"
          @click="onItemClick"
        />

        <!-- KAN-111: per-owner groups (Gone quiet cleanup), each owner's first items shown and the
             rest behind Show all, like the daily report's lists. v-show keeps the rest in the page. -->
        <template v-else-if="section.type === 'groups'">
          <div v-for="group in section.groups" :key="`${section.title}-${group.heading}`" class="section-group">
            <h4 class="section-group__heading">{{ group.heading }}</h4>
            <ul class="mode-list">
              <li
                v-for="(item, index) in group.items"
                :key="`${group.heading}-${index}`"
                v-html="linkifyTicketRefs(item)"
                @click="onItemClick"
              />
            </ul>
            <template v-if="group.rest && group.rest.length">
              <button
                type="button"
                class="section-group__toggle"
                :aria-expanded="isRestOpen(section, group)"
                @click="toggleRest(section, group)"
              >
                {{ isRestOpen(section, group) ? 'Hide' : `Show all ${group.rest.length} more ${group.restNoun || ''}`.trim() }}
              </button>
              <ul v-show="isRestOpen(section, group)" class="mode-list section-group__rest">
                <li
                  v-for="(item, index) in group.rest"
                  :key="`${group.heading}-rest-${index}`"
                  v-html="linkifyTicketRefs(item)"
                  @click="onItemClick"
                />
              </ul>
            </template>
          </div>
        </template>

        <ul v-else class="mode-list">
          <li
            v-for="(item, index) in section.items"
            :key="`${section.title}-${index}`"
            v-html="linkifyTicketRefs(item)"
            @click="onItemClick"
          />
        </ul>

        <dl v-if="section.dataContextItems && section.dataContextItems.length" class="section-data-context">
          <div v-for="item in section.dataContextItems" :key="item.label" class="section-data-context__item">
            <dt>{{ item.label }}</dt>
            <dd>{{ item.value }}</dd>
          </div>
        </dl>
      </div>
    </section>
  </section>
</template>

<script>
import { ticketRefClass } from '@/utils/visitedTickets'
export default {
  name: 'LastWorkingWeekView',
  props: {
    sections: {
      type: Array,
      default: () => [],
    },
    ticketRefs: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['ticket-ref-clicked'],
  data() {
    return {
      expandedSections: {},
      openRest: {},
    }
  },
  methods: {
    isSectionExpanded(title) {
      if (this.expandedSections[title] !== undefined) return this.expandedSections[title]
      return title === 'Summary'
    },
    toggleSection(title) {
      this.expandedSections = {
        ...this.expandedSections,
        [title]: !this.isSectionExpanded(title),
      }
    },
    isRestOpen(section, group) {
      return this.openRest[`${section.title}|${group.heading}`] === true
    },
    toggleRest(section, group) {
      const key = `${section.title}|${group.heading}`
      this.openRest = { ...this.openRest, [key]: !this.openRest[key] }
    },
    linkifyTicketRefs(text) {
      if (!text || !this.ticketRefs.length) return this.escapeHtml(text)
      const ticketNbrs = this.ticketRefs.map((t) => t.ticketNbr).filter(Boolean)
      if (!ticketNbrs.length) return this.escapeHtml(text)
      const escaped = this.escapeHtml(text)
      const pattern = new RegExp(`\\b(${ticketNbrs.map(this.escapeRegex).join('|')})\\b`, 'g')
      return escaped.replace(
        pattern,
        (match) => `<span class="${ticketRefClass(match)}" data-ticket-nbr="${match}" role="button" tabindex="0">${match}</span>`
      )
    },
    escapeHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    onItemClick(event) {
      const ticketNbr = event.target?.dataset?.ticketNbr
      if (ticketNbr) {
        this.$emit('ticket-ref-clicked', ticketNbr)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.last-working-week-view {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.mode-card {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-3;
}

.mode-card--accordion {
  padding: 0;
  overflow: hidden;
}

.accordion-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: $space-3;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: $space-2;

  &:hover {
    background: $color-surface-alt;
  }
}

.accordion-body {
  padding: 0 $space-3 $space-3;
}

.accordion-chevron {
  flex-shrink: 0;
  color: $color-text-muted;
}

.section-label {
  margin: 0;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.mode-text {
  margin: 0;
  white-space: pre-line;
}

.mode-list {
  margin: 0;
  padding-left: 1.2rem;
  list-style-type: disc;
}

.mode-list li + li {
  margin-top: $space-2;
}

:deep(.ticket-ref) {
  color: $color-primary-dark;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $color-primary;
  }

  &.ticket-ref--visited {
    color: $color-text-muted;
  }
}

.section-group + .section-group {
  margin-top: $space-4;
}

.section-group__heading {
  margin: 0 0 $space-2;
  font-size: $font-size-sm;
  font-weight: 700;
  color: $color-text-strong;
}

.section-group__toggle {
  margin-top: $space-2;
  padding: 0;
  border: 0;
  background: none;
  color: $color-primary-dark;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $color-primary;
  }
}

.section-group__rest {
  margin-top: $space-2;
}

.section-data-context {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
  margin: $space-3 0 0;
  padding-top: $space-3;
  border-top: 1px solid $color-border;
}

.section-data-context__item {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.section-data-context__item dt {
  margin: 0;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.section-data-context__item dd {
  margin: 0;
}

@media (max-width: 900px) {
  .section-data-context {
    grid-template-columns: 1fr;
  }
}
</style>
